import React, { useContext, useEffect, useRef, useState } from "react"
import tw from "twin.macro"
import { useLocation } from "@reach/router"
import Headroom from "react-headroom"
import PropTypes from "prop-types"
import { Link } from "gatsby"
import { useQueryParam, NumberParam } from "use-query-params"

import { myContext } from "../context/Provider"
import { pushDatalayerEvent } from "../utils"
import { Button, Container, openChat } from "./auvelity-ui"
import { handleNavClick, isBrowser, convertToSlug } from "../utils"

import AuvelityLogo from "../images/logo/auvelity-logo.svg"
import BurgerIcon from "../images/icons/ic-hamburger.svg"
import CloseIcon from "../images/icons/ic-close.svg"
import createModal from "./modals/createModal"

const MobileSVGBase = ({ children }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 15 24"
    tw="m-auto w-5 h-5  lg:w-6 lg:h-6"
  >
    <g>{children}</g>
  </svg>
)

const dropDownSVG = (
  <MobileSVGBase tw="w-8">
    <g>
      <g transform="rotate(-135 9 4.5)">
        <path fill="#ffc000" d="M3.07 6.07l12-12h-12z" />
      </g>
    </g>
  </MobileSVGBase>
)

const mobileSVG = (
  <MobileSVGBase>
    {" "}
    <path
      d="m8.5.5a8 8 0 1 0 0 16 8 8 0 0 0 0-16"
      fill="#ffc000"
      transform="matrix(-1 0 0 -1 17 17)"
    />
  </MobileSVGBase>
)

// links
const headerLinks = [
  {
    text: (
      <div tw="pointer-events-none">
        <span css={[tw`md:(hidden)`]}>Important Safety&nbsp;Info</span>
        <span css={[tw`hidden md:(inline)`]}>Important Safety Information</span>
      </div>
    ),
    spanishText: (
      <div tw="pointer-events-none">
        <span tw="inline">Informacion de Seguridad Importante</span>
      </div>
    ),
    link: "#isi",
    spanishLink: "#isi",
    external: false,
    classForGTM: "gtm-navigation fl-isi-open",
    elementName: "",
  },

  {
    text: (
      <span tw="pointer-events-none md:whitespace-nowrap">
        Full&nbsp;Prescribing Information{" "}
        <span css={[tw`sr-only`]}>(link to PDF)</span>
      </span>
    ),
    spanishText: (
      <span tw="pointer-events-none md:whitespace-nowrap">
        Ficha técnica completa
      </span>
    ),
    link: "https://www.axsome.com/auvelity-prescribing-information.pdf",
    prescribing: true,
    spanishLink:
      "https://www.axsome.com/auvelity-prescribing-information-es.pdf",
    external: false,
    classForGTM: "gtm-file-download gtm-cta-click fl-prescribing-info",
  },
  {
    text: (
      <>
        Medication Guide <span css={[tw`sr-only`]}>(link to PDF)</span>
      </>
    ),
    spanishText: <>Guía del medicamento</>,
    link: "https://www.axsome.com/auvelity-medication-guide.pdf",
    spanishLink: "https://www.axsome.com/auvelity-medication-guide-es.pdf",
    external: false,
    medication: true,
    classForGTM:
      "gtm-file-download gtm-cta-click fl-med-guide fl-med-guide-header",
  },
  {
    text: <>For Healthcare Professionals</>,
    link: "https://auvelityhcp.com",
    external: false,
  },
]

const redirectLinks = [
  {
    text: (
      <>
        For Healthcare
        <br tw="hidden sm:block lg:hidden" /> Professionals
      </>
    ),
    link: "https://auvelityhcp.com",
    external: false,
    classForGTM: "gtm-navigation fl-exit-hcp",
  },
]

const navLinks = [
  // double spaces on text are used as line-breaks on main nav render
  {
    text: "About Depression",
    link: "/about-depression",
    external: false,
    classForGTM: "gtm-navigation",
    width: tw`w-16 lg:w-28`,
    activeIcon: (
      <path
        d="m8.5.5a8 8 0 1 0 0 16 8 8 0 0 0 0-16"
        fill="#ffc000"
        transform="matrix(-1 0 0 -1 17 17)"
      />
    ),
  },
  {
    text: "Why AUVELITY?",
    link: "/how-auvelity-works",
    external: false,
    classForGTM: "gtm-navigation",
    width: tw`w-16 lg:w-28`,
    activeIcon: (
      <path
        d="m8.5.5a8 8 0 1 0 0 16 8 8 0 0 0 0-16"
        fill="#ffc000"
        transform="matrix(-1 0 0 -1 17 17)"
      />
    ),
    subnav: [
      {
        text: "How AUVELITY Works",
        classForGTM: "gtm-navigation",
        link: "/how-auvelity-works",
      },
      {
        text: "How AUVELITY May Help",
        classForGTM: "gtm-navigation",
        link: "/how-auvelity-may-help",
      },
      {
        text: "Taking AUVELITY",
        classForGTM: "gtm-navigation",
        link: "/taking-auvelity",
      },
      {
        text: "Side Effects",
        classForGTM: "gtm-navigation",
        link: "/side-effects",
      },
    ],
    show: true,
  },
  {
    text: "Patient Stories",
    link: "/patient-stories",
    external: false,
    classForGTM: "gtm-navigation",
    width: tw`w-16 lg:w-28`,
    activeIcon: (
      <path
        d="m8.5.5a8 8 0 1 0 0 16 8 8 0 0 0 0-16"
        fill="#ffc000"
        transform="matrix(-1 0 0 -1 17 17)"
      />
    ),
    show: false,
    subnav: [
      {
        text: "Cindy",
        classForGTM: "gtm-navigation",
        link: "/cindy",
      },
      {
        text: "Danielle",
        classForGTM: "gtm-navigation",
        link: "/danielle",
      },
      {
        text: "Mecca",
        classForGTM: "gtm-navigation",
        link: "/mecca",
      },
      {
        text: "Ryan",
        classForGTM: "gtm-navigation",
        link: "/ryan",
      }
    ],
  },
  {
    text: "Support",
    link: "/resources",
    external: false,
    classForGTM: "gtm-navigation",
    width: tw`w-16 lg:w-28`,
    activeIcon: (
      <path
        d="m8.5.5a8 8 0 1 0 0 16 8 8 0 0 0 0-16"
        fill="#ffc000"
        transform="matrix(-1 0 0 -1 17 17)"
      />
    ),
    subnav: [
      {
        text: "Resources & Downloads",
        link: "/resources",
        classForGTM: "gtm-navigation",
      },
      {
        text: <>Auvelity&reg; On My Side</>,
        link: "/auvelity-on-my-side",
        classForGTM: "gtm-navigation",
      },
      {
        text: "Connect With a Healthcare Provider",
        link: "/connect-with-a-provider",
        classForGTM: "gtm-navigation",
      },
      {
        text: "Savings Card",
        link: "/savings-card",
        classForGTM: "gtm-navigation",
      },
      {
        text: "AUVELITY Ambassador Program",
        link: "/calling-all-advocates",
        classForGTM: "gtm-navigation",
      }
    ],
    show: true,
  },
  {
    text: "FAQs",
    link: "/faqs",
    external: false,
    classForGTM: "gtm-navigation",
    width: tw`w-16 lg:w-28`,
    activeIcon: (
      <path
        d="m8.5.5a8 8 0 1 0 0 16 8 8 0 0 0 0-16"
        fill="#ffc000"
        transform="matrix(-1 0 0 -1 17 17)"
      />
    ),
  }
]

// Styles

const HeaderLinkBar = tw.div`py-2 flex justify-center leading-snug items-center lg:(justify-end tracking-tight py-0)`

const Header = ({ handleCookie, cookieVisible, isThankYouPage=false }) => {
  const location = useLocation()
  const { pathname } = location
  const splitLocation = pathname.split("/")

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileMenuHeight, setMobileMenuHeight] = useState(0)

  const context = useContext(myContext)
  const headerRef = useRef()

  const [dropdownParam] = useQueryParam("dropdown", NumberParam)
  const [mobileMenuParam] = useQueryParam("mobileMenu", NumberParam)

  const toggleMobileMenu = () => {
    context.setMobileMenu(!context.mobileMenu)
  }

  const setActiveParent = () => {
    if (isBrowser) {
      const path = window.location.pathname

      navLinks.forEach(links => {
        // console.log("context.activeParent", context.activeParent)
        // console.log("links.subnav", links)
        if (links.subnav && links.show) {
          links.subnav.forEach(pathname => {
            if (path === pathname.link) {
              context.setActiveParent(links.link)
            }
          })
        } else {
          path === links.link && context.setActiveParent(links.link)
          path === "/" && context.setActiveParent("")
        }
      })
    }
  }

  // removing context functionality for now to keep all language english

  useEffect(() => {
    // splitLocation[1] === "es"
    //   ? context.setActiveSpanishPage(true)
    //   : context.setActiveSpanishPage(false)
    setActiveParent()
    if (context.isMobile) {
      context.setDisableHeader(false)
    }
  }, [])

  useEffect(() => {
    setDropdownOpen(dropdownParam)
  }, [dropdownParam])

  useEffect(() => {
    context.setIndicationOpen(false)
    context.setSafetyOpen(false)
  }, [context.mobileMenu])

  useEffect(() => {
    // this would intermittently throw an error on refresh
    // so I've set it to only get the dimensions once it finds the headerRef in the first place
    const headerBox = headerRef.current
      ? headerRef.current.getBoundingClientRect()
      : {}
    // bump back to the top if we've only scrolled a small way down
    if (context.mobileMenu && headerBox.top !== 0) {
      window.scrollTo(0, 0)
    }
    context.setHeaderHeight(headerBox.height)
    // mobile menu never QUITE opens properly the first time when we use context.headerHeight here
    // so we're using the local variable, even though we've set it.
    const calculatedHeight = window.innerHeight - headerBox.height
    setMobileMenuHeight(calculatedHeight)
  }, [context.mobileMenu, cookieVisible])

  useEffect(() => {
    // wait for header height before updating UI
    if (mobileMenuParam === 1) {
      context.setMobileMenu(true)
    }
  }, [context.headerHeight])

  const Dropdown = ({
    title,
    dropDownIcon,
    children,
    subnav,
    isOpenFlag = false,
    addClassForGTM,
  }) => {
    const [isOpen, setOpen] = useState(isOpenFlag)
    const idPrefix = convertToSlug(title)

    const ElementBase = subnav.isSubnav ? "button" : "a"

    return (
      <div tw="text-center my-6">
        <ElementBase
          id={`${idPrefix}-toggle`}
          className={`${addClassForGTM}`}
          data-gtm-location="main menu"
          aria-controls={idPrefix}
          aria-expanded={isOpen}
          onClick={e => {
            if (subnav.isSubnav) {
              setOpen(!isOpen)
            } else {
              handleNavClick(e, subnav.link)
              toggleMobileMenu()
            }
          }}
          css={[isOpen && tw``]}
        >
          <div css={[tw`hidden md:(block)`, isOpen && tw`md:(visible)`]}>
            {mobileSVG}
          </div>
          <span
            tw="text-blue tracking-tight text-center mb-1"
            activestyle={tw`font-bold`}
          >
            {title}
          </span>
          <div css={[tw`text-center`, isOpen && tw`transform rotate-180`]}>
            {dropDownIcon}
          </div>
        </ElementBase>

        <div
          id={idPrefix}
          aria-labelledby={`${idPrefix}-toggle`}
          css={[isOpen ? tw`block relative` : tw`hidden`, tw`mt-4`]}
        >
          <div>{children}</div>
        </div>
      </div>
    )
  }
  const rawHTML = '<div data-tw="bg-lightgrey" class="thank-you-header css-7t9u5r-Header"> <div class="css-6515ff-Container etw1b1c4"> <div data-tw="flex-grow px-0" class="css-5qekrl-Header"> <div class="css-1r7mjtb-HeaderLinkBar header-thank-you eal8s4w0" style="align-items: center;"><a class="headerLink gtm-navigation fl-isi-open css-15t7bg7-Header" href="/#isi" rel="noopener noreferrer" data-gtm-location="main menu" data-tw="py-1 px-2 text-blue text-xs text-center border-l border-grey hover:font-bold first:border-l-0 md:(flex-1 border-0 text-ms py-2) lg:(px-4 flex-initial)">Important Safety&nbsp;Info<span class="desktop-only-thanks">rmation</span></a><a class="headerLink gtm-navigation fl-isi-open css-15t7bg7-Header" href="https://www.axsome.com/wp-content/uploads/2024/11/auvelity-prescribing-information.pdf" target="_blank" rel="noopener noreferrer" data-gtm-location="main menu" data-tw="py-1 px-2 text-blue text-xs text-center border-l border-grey hover:font-bold first:border-l-0 md:(flex-1 border-0 text-ms py-2) lg:(px-4 flex-initial)">Full Prescribing Information and&nbsp;Boxed&nbsp;Warning</a></div> </div> </div> </div>';
  return (
    <>
      <Headroom
        id="header"
        disable={context.disableHeader}
        tw="bg-white"
        style={{
          zIndex: "14",
        }}
      >
        <header css={[tw`bg-white border-b border-grey`]} ref={headerRef} id="main-header" role="banner">
          <div tw="bg-lightgrey">
            <Container>
              <div tw="flex-grow px-0">
                {!isThankYouPage ?
                  <HeaderLinkBar>
                    {headerLinks.map((headerLink, index) => (
                      <a
                        className= { "headerLink " + headerLink.classForGTM}
                        href={
                          context.activeSpanishPage
                            ? headerLink.spanishLink
                            : headerLink.link
                        }
                        css={[
                          tw`py-1 px-2 text-blue text-xs text-center border-l border-grey hover:font-bold first:border-l-0 md:(flex-1 border-0 text-ms py-2) lg:(px-4 flex-initial)`,
                          index === headerLinks.length - 1 && tw`lg:hidden`,
                          context.activeSpanishPage && tw`px-[0.1rem] sm:px-2`,
                          context.activeSpanishPage &&
                            headerLink.spanishText === undefined &&
                            tw`hidden`,
                        ]}
                        key={index}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => {
                          if(headerLink.medication) {
                            pushDatalayerEvent({
                              event: "ViewContent",
                            })
                          }
                          if(headerLink.prescribing) {
                            pushDatalayerEvent({
                              event: "PrescribingInfo",
                            })
                          }
                          handleNavClick(e)}
                        }
                        data-gtm-location={
                          context.activeSpanishPage ? "top menu" : "main menu"
                        }
                      >
                        {context.activeSpanishPage
                          ? headerLink.spanishText
                          : headerLink.text}
                      </a>
                    ))}
                    {!context.activeSpanishPage &&
                      redirectLinks.map((redirectLink, index) => (
                        <a
                          className={`${redirectLink.classForGTM}`}
                          href={redirectLink.link}
                          key={index}
                          css={[
                            tw`py-1 px-2 text-blue text-xs text-center border-l border-grey hidden md:block hover:hover-bold-blue first:border-l-0 md:(flex-1 border-0 text-ms py-2) lg:(px-4 flex-initial)`,
                          ]}
                          data-gtm-location="main menu"
                        >
                          {redirectLink.text}
                        </a>
                      ))}
                  </HeaderLinkBar>
                  :
                  <div dangerouslySetInnerHTML={{ __html: rawHTML }} />
                }
              </div>
            </Container>
          </div>
          <Container>
            <div tw="flex-grow ">
              <div
                css={[
                  tw`py-3 flex justify-between items-center w-full flex-wrap md:(p-0)`,
                  context.activeSpanishPage &&
                    tw`py-[12px] sm:py-[28px] lg:py-[31.5px] xl:py-[15px]`,
                ]}
              >
                <Link
                  to="/"
                  tw="my-auto flex-none"
                  className="gtm-navigation"
                  data-gtm-location="Home logo"
                >
                  <img
                    src={AuvelityLogo}
                    alt="Auvelity® (dextromethorphan HBr and bupropion HCl) homepage"
                    tw="h-16 md:h-16 xl:(w-[198px] h-24)"
                    width="198"
                    height="172"
                  />
                </Link>

                <button
                  id="mobile-menu-toggle"
                  aria-expanded={context.mobileMenu}
                  aria-controls="mobile-menu"
                  tw="lg:hidden block -webkit-touch-callout[none]"
                  onClick={toggleMobileMenu}
                >
                  <img
                    src={!context.mobileMenu ? BurgerIcon : CloseIcon}
                    alt="Menu"
                    tw="p-2"
                  />
                </button>
                {!context.activeSpanishPage &&
                  navLinks.map((navLink, i) => {
                    {/* console.log("context.activeParent", context.activeParent) */}
                    {/* console.log("navLink.link", navLink.link) */}
                    return (
                      <div
                        css={[
                          tw`hidden pb-16 relative text-sm mx-2 flex-col items-center text-center justify-end whitespace-nowrap text-blue lg:(py-12 block) lg:(text-base mx-0) xl:text-lg  hover:font-bold tracking-tight`,
                        ]}
                        className="group"
                        key={i}
                      >
                        <Link
                          to={navLink.link}
                          css={[
                            tw`block focus:outline-none focus-visible:(ring rounded ring-blue)`,
                            context.activeParent === navLink.link &&
                              tw`font-bold`,
                          ]}
                          className="gtm-navigation"
                          data-gtm-location="main menu"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 18 24"
                            css={[
                              tw`absolute left-0 right-0 top-7 invisible m-auto w-5 h-5 opacity-0 transition lg:(w-6 h-6)`,
                              context.activeParent === navLink.link &&
                                tw`visible opacity-100`,
                            ]}
                          >
                            <g>{navLink.activeIcon}</g>
                          </svg>
                          <div tw="no-shift leading-[1.3em]" linktext={navLink.text}>
                            {navLink.text.split('  ').map((word, index) => ( // split at the double space in the string 
                              <React.Fragment key={index}>
                                {word}
                                {<br />}
                              </React.Fragment>
                            ))}
                          </div>
                        </Link>
                        {navLink.subnav && navLink.show && (
                          <ul
                            className="subNav"
                            css={[
                              tw`absolute z-index[10] max-h-0 rounded-br-3xl text-center bg-lightgrey overflow-hidden flex-col items-center min-width[16rem] transition-max-height duration-500 ease-in-out flex left-1/2 transform -translate-x-1/2 top-full focus-within:(max-h-96 visible) group-hover:( max-h-96 visible )`,

                              ((dropdownOpen === 1 && i === 0) ||
                                (dropdownOpen === 2 && i === 2)) &&
                                tw`max-h-96 visible`,
                            ]}
                          >
                            {navLink.subnav &&
                              navLink.subnav.map((subnavLink, index) => (
                                <li key={index}>
                                  <Link
                                    to={subnavLink.link}
                                    css={[
                                      tw`px-6 py-3 my-2 font-normal block focus:outline-none focus-visible:ring-2 focus-visible:rounded focus-visible:ring-blue lg:(text-base hover:font-bold) group-hover:(block)`,
                                    ]}
                                    className="gtm-navigation"
                                    data-gtm-location="main submenu"
                                  >
                                    <div tw="no-shift">
                                      <p tw="cursor-pointer">{subnavLink.text}</p>
                                    </div>
                                  </Link>
                                  {index !== navLink.subnav.length - 1 && (
                                    <div tw="border-b-2 w-16 h-0 mx-auto">
                                      &nbsp;
                                    </div>
                                  )}
                                </li>
                              ))}
                          </ul>
                        )}
                        {navLink.subnav && navLink.show && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 15 24"
                            tw="absolute right-0 left-0 m-auto w-5 h-5 transform transition lg:(w-6 h-6) group-hover:(-rotate-180)"
                            css={[
                              ((dropdownParam === 1 && i === 0) ||
                                (dropdownParam === 2 && i === 2)) &&
                                tw`-rotate-180`,
                            ]}
                          >
                            <g>
                              <g>
                                <g transform="rotate(-135 9 4.5)">
                                  <path
                                    fill="#ffc000"
                                    d="M3.07 6.07l12-12h-12z"
                                  />
                                </g>
                              </g>
                            </g>
                          </svg>
                        )}
                      </div>
                    )
                  })}
              </div>

              {/* mobile menu */}
              {context.mobileMenu && (
                <nav
                  id="mobile-menu"
                  aria-labelledby="mobile-menu-toggle"
                  css={[
                    tw`bg-white absolute inset-x-0 bottom-0 block text-center px-6 pb-6 pt-6 overflow-y-auto lg:hidden`,
                    `height: ${mobileMenuHeight}px; top: ${context.headerHeight}px`,
                  ]}
                >
                  {!context.activeSpanishPage &&
                    navLinks.map((navLink, i) => (
                      <Dropdown
                        key={i}
                        title={navLink.text}
                        dropDownIcon={navLink.subnav && navLink.show && dropDownSVG}
                        subnav={{ isSubnav: navLink.subnav && navLink.show, link: navLink.link }}
                        isOpenFlag={
                          (dropdownParam === 1 && i === 0) ||
                          (dropdownParam === 2 && i === 2)
                        }
                        addClassForGTM="gtm-navigation"
                      >
                        <ul tw="bg-lightgrey rounded-br-3xl rounded-tl-3xl">
                          {navLink.subnav &&
                            navLink.subnav.map((subnavLink, index) => (
                              <li tw="p-4 border-b-2 last:border-b-0" key={index}>
                                <Link
                                  tw="text-blue"
                                  to={subnavLink.link}
                                  className="gtm-navigation"
                                  data-gtm-location="main submenu"
                                  onClick={toggleMobileMenu}
                                >
                                  {subnavLink.text}
                                </Link>
                              </li>
                            ))}
                        </ul>
                      </Dropdown>
                    ))}
                  {!context.activeSpanishPage && (
                    <>
                      <div tw="mb-6 mx-auto w-[90%]" className="nurse-nav">
                        <Button
                          as="link"
                          size="sm"
                          variant="yellow"
                          arrow
                          target="_blank"
                          rel="noopener noreferrer"
                          styles={tw`w-full flex items-center justify-between`}
                          addClassForGTM="gtm-external-link"
                          onClick={e => {
                            openChat();
                            pushDatalayerEvent({
                              event: "Contact",
                            })
                            toggleMobileMenu()
                          }}
                        >
                          Talk to a Nurse Navigator
                        </Button>
                      </div>
                      <div tw="mb-6 mx-auto w-[90%]" className="savings-card-nav">
                        <Button
                          as="link"
                          size="sm"
                          arrow
                          variant="darkgrey"
                          href="/savings-card"
                          onClick={e => {
                            pushDatalayerEvent({
                              event: "InitiateCheckout",
                            })
                            handleNavClick(e, "/savings-card")
                            toggleMobileMenu()
                          }}
                          styles={tw`w-full flex items-center justify-between`}
                        >
                          Savings Card
                        </Button>
                      </div>
                      <div tw="mb-6 mx-auto w-[90%]" className="patient-stories-nav">
                        <Button
                          as="link"
                          size="sm"
                          variant="grey"
                          arrow
                          target="_blank"
                          rel="noopener noreferrer"
                          styles={tw`w-full flex items-center justify-between`}
                          addClassForGTM="gtm-external-link"
                          onClick={e => {
                            pushDatalayerEvent({
                              event: "PatientStories",
                            })
                            handleNavClick(e, "/patient-stories")
                            toggleMobileMenu()
                          }}
                        >
                          Hear patient stories
                        </Button>
                      </div>
                    </>
                  )}
                  {context.activeSpanishPage && (
                    <div tw="w-[260px] mx-auto">
                      <span tw="my-1 w-full">
                        <Button
                          arrow
                          variant="yellow"
                          as="link"
                          size="sm"
                          href="/"
                          fillWidth
                          back
                          addClassForGTM="gtm-cta-click"
                          data-gtm-location="menu"
                        >
                          Volver al sitio en inglés
                        </Button>
                      </span>
                    </div>
                  )}
                </nav>
              )}
            </div>
          </Container>
        </header>
      </Headroom>
    </>
  )
}

Header.propTypes = {
  siteTitle: PropTypes.string,
}

Header.defaultProps = {
  siteTitle: ``,
}

export default Header